import { isBetaEnabled } from '~/common/app/beta';
import { HttpError } from '~/common/http/http-error';

export function apiFetch(path: string, init?: RequestInit): Promise<Response> {
  return fetch(withBaseUrl(path), withBetaHeader(init));
}

export async function jsonFetch<T = void>(
  path: string,
  init?: JsonRequestInit,
): Promise<T> {
  const response = await apiFetch(path, mapJsonInit(init));

  if (!response.ok) throw await HttpError.from(response);

  return parseJsonBody(response);
}

/**
 * Some endpoints return 200 with no body, which `response.json()` throws on.
 * Read the body as text first and only parse it if it's non-empty.
 */
async function parseJsonBody<T = void>(response: Response): Promise<T> {
  const text = await response.text();
  if (text === '') return undefined as T;
  return JSON.parse(text) as T;
}

function withBaseUrl(path: string): string {
  return import.meta.env.VITE_API_PREFIX + path;
}

function withBetaHeader(init?: RequestInit): RequestInit | undefined {
  if (!isBetaEnabled()) return init;

  const headers = new Headers(init?.headers);
  headers.set('insights-beta', 'true');

  return { ...init, headers };
}

function mapJsonInit(init?: JsonRequestInit): RequestInit {
  const headers = new Headers(init?.headers);
  headers.set('Content-Type', 'application/json');

  const body = init?.body === undefined ? undefined : JSON.stringify(init.body);

  return { ...init, headers, body };
}

export type JsonRequestInit = Omit<RequestInit, 'body'> & {
  body?: unknown;
};
